import { useState } from "react";
import { ArrowRightLeft } from "lucide-react";
import { api, type AccountRecord, type TransferRecord } from "./api";
import { todayKey } from "./income-plan";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
export function TransferDialog({
  accounts,
  item,
  initialDate,
  initialStatus,
  close,
  refresh,
}: {
  accounts: AccountRecord[];
  item?: TransferRecord;
  initialDate?: string;
  initialStatus?: TransferRecord['status'];
  close: () => void;
  refresh: () => Promise<void>;
}) {
  const [source, setSource] = useState(
    item?.sourceAccountId || accounts[0]?.id || "",
  );
  const [destination, setDestination] = useState(
    item?.destinationAccountId || accounts[1]?.id || "",
  );
  const [description, setDescription] = useState(
    item?.description || "Transferência entre contas",
  );
  const [amount, setAmount] = useState(item ? String(item.amount) : "");
  const [date, setDate] = useState(
    item?.transactionDate.slice(0, 10) || initialDate || todayKey(),
  );
  const [status, setStatus] = useState(item?.status || initialStatus || "completed");
  const [notes, setNotes] = useState(item?.notes || "");
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  return (
    <Dialog open onOpenChange={(v) => !v && !busy && close()}>
      <DialogContent className="quick-modal">
        <DialogHeader>
          <DialogTitle>
            {item ? "Editar transferência" : "Transferir entre contas"}
          </DialogTitle>
          <DialogDescription>
            Registre a saída de uma conta e a entrada na outra. O total do seu
            dinheiro não muda. Este registro não envia dinheiro pelo banco.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setError("");
            setBusy(true);
            try {
              await api.transfer(
                {
                  sourceAccountId: source,
                  destinationAccountId: destination,
                  description,
                  amount: Number(amount.replace(",", ".")),
                  transactionDate: date,
                  status,
                  notes,
                },
                item?.id,
              );
              await refresh();
              toast.success("Transferência registrada");
              close();
            } catch (e) {
              setError(
                e instanceof Error ? e.message : "Não foi possível salvar",
              );
            } finally {
              setBusy(false);
            }
          }}
        >
          <div className="form-grid">
            {(
              [
                ["Conta de origem", source, setSource],
                ["Conta de destino", destination, setDestination],
              ] as const
            ).map(([label, value, change]) => (
              <div key={label}>
                <Label>{label}</Label>
                <Select value={value} onValueChange={change}>
                  <SelectTrigger aria-label={label}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <Label htmlFor="transfer-description">Descrição</Label>
          <Input
            id="transfer-description"
            required
            maxLength={120}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Label htmlFor="transfer-amount">Valor (R$)</Label>
          <Input
            id="transfer-amount"
            required
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <Label htmlFor="transfer-date">Data</Label>
          <Input
            id="transfer-date"
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <Label>Status</Label>
          <Select
            value={status}
            onValueChange={(v) => setStatus(v as typeof status)}
          >
            <SelectTrigger aria-label="Status da transferência">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="completed">Realizada</SelectItem>
              <SelectItem value="planned">Prevista</SelectItem>
              <SelectItem value="cancelled">Cancelada</SelectItem>
            </SelectContent>
          </Select>
          <Label htmlFor="transfer-notes">Nota (opcional)</Label>
          <Input
            id="transfer-notes"
            maxLength={500}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          {source === destination && (
            <p className="form-notice">Escolha duas contas diferentes.</p>
          )}
          {accounts.length < 2 && (
            <p className="form-notice">
              Cadastre pelo menos duas contas para transferir.
            </p>
          )}
          {error && (
            <p role="alert" className="form-error">
              {error}
            </p>
          )}
          <Button
            type="submit"
            variant="liquid"
            disabled={busy || accounts.length < 2 || source === destination}
          >
            {busy ? "Salvando…" : "Salvar transferência"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
